import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Clock, 
  Users, 
  MapPin, 
  ChevronRight,
  Check,
  Calendar,
  Shield,
  Star,
  Sparkles,
  AlertCircle,
  Heart,
  Share2,
  Info,
  CheckCircle,
  CreditCard,
  Zap
} from 'lucide-react';
import { EmbedLayout, LandingLayout } from '@/components/layout';
import { useDisplayMode } from '@/hooks/useDisplayMode';
import { usePropertyContext } from '@/context/PropertyContext';
import { api, type ExperienceDetail } from '@/lib/api';
import { cn, formatPrice } from '@/lib/utils';
import { trackExperienceViewed, trackBookingStarted, startAbandonmentTracking, updateAbandonmentStep } from '@/lib/analytics';

/**
 * Format duration in minutes to a human-readable string
 */
function formatDuration(minutes: number | null | undefined): string {
  if (!minutes || isNaN(minutes)) return 'Duration varies';
  
  if (minutes < 60) {
    return `${minutes} min`;
  }
  
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  if (mins === 0) {
    return hours === 1 ? '1 hour' : `${hours} hours`;
  }
  
  return `${hours}h ${mins}m`;
}

/**
 * Format guest count range
 */
function formatGuestRange(min: number | null | undefined, max: number | null | undefined): string {
  const minGuests = min || 1;
  const maxGuests = max || 10;
  
  if (minGuests === maxGuests) {
    return `${maxGuests} ${maxGuests === 1 ? 'guest' : 'guests'}`;
  }
  
  return `${minGuests}–${maxGuests} guests`;
}

/**
 * Experience detail page - Premium, conversion-optimized design
 * Designed for $100M+ hospitality brands
 */
export function ExperiencePage() {
  const displayMode = useDisplayMode();
  const { propertyKey, config } = usePropertyContext();
  const params = useParams<{ experienceSlug: string; propertyKey?: string }>();
  const [experience, setExperience] = useState<ExperienceDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Save and Share state
  const [isSaved, setIsSaved] = useState(false);
  const [shareStatus, setShareStatus] = useState<'idle' | 'copied' | 'shared'>('idle');

  const key = params.propertyKey || propertyKey;
  const slug = params.experienceSlug;
  
  // Check if experience is saved in localStorage on mount
  useEffect(() => {
    if (slug) {
      const savedExperiences = JSON.parse(localStorage.getItem('el_saved_experiences') || '[]');
      setIsSaved(savedExperiences.includes(slug));
    }
  }, [slug]);

  // Handle save/unsave experience
  const handleSave = useCallback(() => {
    if (!slug) return;
    
    const savedExperiences = JSON.parse(localStorage.getItem('el_saved_experiences') || '[]');
    
    if (isSaved) {
      const updated = savedExperiences.filter((s: string) => s !== slug);
      localStorage.setItem('el_saved_experiences', JSON.stringify(updated));
      setIsSaved(false);
    } else {
      savedExperiences.push(slug);
      localStorage.setItem('el_saved_experiences', JSON.stringify(savedExperiences));
      setIsSaved(true);
    }
  }, [slug, isSaved]);

  // Handle share experience
  const handleShare = useCallback(async () => {
    if (!experience) return;
    
    const shareUrl = window.location.href;
    const shareData = {
      title: experience.name,
      text: experience.shortDescription || `Check out ${experience.name}`,
      url: shareUrl,
    };
    
    if (navigator.share && navigator.canShare?.(shareData)) {
      try {
        await navigator.share(shareData);
        setShareStatus('shared');
        setTimeout(() => setShareStatus('idle'), 2000);
        return;
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
      }
    }
    
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareStatus('copied');
      setTimeout(() => setShareStatus('idle'), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [experience]);

  useEffect(() => {
    async function fetchExperience() {
      if (!slug) {
        setError('Experience not found');
        setIsLoading(false);
        return;
      }

      if (!key) {
        setError('No property specified. Please access this page from a property demo.');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const data = await api.getExperience(key, slug);
        setExperience(data);

        if (data) {
          trackExperienceViewed(data.id, data.name);
          startAbandonmentTracking('experience', data.id);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load experience');
      } finally {
        setIsLoading(false);
      }
    }

    fetchExperience();
  }, [key, slug]);

  const Layout = displayMode === 'embed' ? EmbedLayout : LandingLayout;
  const backLink = displayMode === 'embed' ? `/embed/${key}` : '/';

  // Preserve property query param in book link for demo mode
  const getBookLink = () => {
    if (displayMode === 'embed') {
      return `/embed/${key}/${slug}/book`;
    }
    // Check if we have a property query param (demo mode)
    const searchParams = new URLSearchParams(window.location.search);
    const propertyParam = searchParams.get('property');
    if (propertyParam) {
      return `/${slug}/book?property=${propertyParam}`;
    }
    return `/${slug}/book`;
  };

  const bookLink = getBookLink();

  if (isLoading) {
    return (
      <Layout>
        <LoadingSkeleton />
      </Layout>
    );
  }

  if (error || !experience) {
    return (
      <Layout>
        <ErrorState message={error || 'Experience not found'} backLink={backLink} />
      </Layout>
    );
  }

  const coverImage = experience.coverImage || experience.images?.[0]?.url;
  const hasIncluded = experience.included && experience.included.length > 0;
  const hasHighlights = experience.highlights && experience.highlights.length > 0;
  const hasRequirements = experience.requirements && experience.requirements.length > 0;
  const hasWhatToBring = experience.whatToBring && experience.whatToBring.length > 0;
  const hasAddOns = experience.addOns && experience.addOns.length > 0;
  const hasTags = experience.tags && experience.tags.length > 0;

  return (
    <Layout>
      <div className="mx-auto max-w-6xl animate-fade-in">
        {/* Breadcrumb Navigation */}
        <nav className="mb-6">
          <Link
            to={backLink}
            className="group inline-flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
            Back to all experiences
          </Link>
        </nav>

        {/* Mobile Hero Image - Immersive */}
        {coverImage && (
          <div className="relative mb-8 overflow-hidden rounded-2xl lg:hidden shadow-luxury">
            <img
              src={coverImage}
              alt={experience.name}
              className="aspect-[4/3] w-full object-cover"
            />
            {/* Premium overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
            <ImageOverlayBadges experience={experience} />
            
            {/* Title overlay on mobile */}
            <div className="absolute bottom-0 left-0 right-0 p-5">
              {experience.category && (
                <span className="inline-flex items-center gap-1.5 rounded-full glass-dark px-3 py-1.5 text-xs font-medium text-white/90 mb-3">
                  <span className="text-sm">{experience.category.icon}</span>
                  {experience.category.name}
                </span>
              )}
              <h1 className="font-serif text-2xl font-medium text-white leading-tight">
                {experience.name}
              </h1>
            </div>
          </div>
        )}

        <div className="grid gap-8 lg:grid-cols-12 lg:gap-10">
          {/* Main Content */}
          <div className="lg:col-span-7 xl:col-span-8">
            {/* Desktop Hero Image - Immersive */}
            {coverImage && (
              <div className="relative mb-8 hidden overflow-hidden rounded-2xl lg:block shadow-luxury">
                <img
                  src={coverImage}
                  alt={experience.name}
                  className="aspect-[16/10] w-full object-cover"
                />
                {/* Premium gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                <ImageOverlayBadges experience={experience} />
              </div>
            )}

            {/* Title & Quick Info */}
            <header className="mb-10">
              {/* Category - Desktop only (mobile shows on image) */}
              {experience.category && (
                <div className="mb-4 hidden lg:block">
                  <span className="badge-premium">
                    <span className="text-base">{experience.category.icon}</span>
                    {experience.category.name}
                  </span>
                </div>
              )}
              
              {/* Title - Elegant serif */}
              <h1 className="hidden lg:block font-serif text-4xl font-medium tracking-tight text-foreground lg:text-[2.75rem] lg:leading-tight">
                {experience.name}
              </h1>
              
              {/* Short Description */}
              {experience.shortDescription && (
                <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
                  {experience.shortDescription}
                </p>
              )}

              {/* Quick Stats Bar - Premium Cards */}
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <StatCard 
                  icon={Clock} 
                  value={formatDuration(experience.durationMinutes)}
                  label="Duration"
                />
                <StatCard 
                  icon={Users} 
                  value={formatGuestRange(experience.minGuests, experience.maxGuests)}
                  label="Group size"
                />
                {experience.averageRating && experience.totalReviews > 0 && (
                  <StatCard
                    icon={Star}
                    value={`${experience.averageRating.toFixed(1)}`}
                    label={`${experience.totalReviews} reviews`}
                    iconClassName="fill-rating text-rating"
                  />
                )}
              </div>
            </header>

            {/* Content Sections - Refined typography */}
            <div className="space-y-12">
              {/* About */}
              {experience.description && (
                <ContentSection title="About this experience">
                  <p className="whitespace-pre-line leading-relaxed text-muted-foreground text-[15px]">
                    {experience.description}
                  </p>
                </ContentSection>
              )}

              {/* Highlights */}
              {hasHighlights && (
                <ContentSection title="Experience highlights" icon={Sparkles} iconColor="text-primary">
                  <div className="grid gap-3 sm:grid-cols-2">
                    {experience.highlights!.map((item, i) => (
                      <CheckItem key={i} text={item} variant="highlight" />
                    ))}
                  </div>
                </ContentSection>
              )}

              {/* What's Included */}
              {hasIncluded && (
                <ContentSection title="What's included">
                  <div className="grid gap-3 sm:grid-cols-2">
                    {experience.included!.map((item, i) => (
                      <CheckItem key={i} text={item} variant="included" />
                    ))}
                  </div>
                </ContentSection>
              )}

              {/* Requirements */}
              {hasRequirements && (
                <ContentSection title="Requirements" icon={Info} iconColor="text-muted-foreground">
                  <ul className="space-y-3">
                    {experience.requirements!.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-muted-foreground">
                        <span className="mt-2.5 h-1 w-1 shrink-0 rounded-full bg-muted-foreground/50" />
                        <span className="text-[15px]">{item}</span>
                      </li>
                    ))}
                  </ul>
                </ContentSection>
              )}

              {/* What to Bring */}
              {hasWhatToBring && (
                <ContentSection title="What to bring">
                  <ul className="space-y-3">
                    {experience.whatToBring!.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-muted-foreground">
                        <span className="mt-2.5 h-1 w-1 shrink-0 rounded-full bg-muted-foreground/50" />
                        <span className="text-[15px]">{item}</span>
                      </li>
                    ))}
                  </ul>
                </ContentSection>
              )}

              {/* Meeting Point */}
              {experience.meetingPoint && (
                <ContentSection title="Meeting point" icon={MapPin} iconColor="text-primary">
                  <p className="text-muted-foreground text-[15px]">{experience.meetingPoint}</p>
                </ContentSection>
              )}

              {/* Tags */}
              {hasTags && (
                <div className="flex flex-wrap gap-2 pt-8 border-t border-border/50">
                  {experience.tags!.map((tag) => (
                    <span 
                      key={tag} 
                      className="rounded-full bg-muted/80 px-3.5 py-1.5 text-xs font-medium text-muted-foreground capitalize tracking-wide"
                    >
                      {tag.replace(/-/g, ' ')}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Booking Sidebar - Premium Design */}
          <aside className="lg:col-span-5 xl:col-span-4">
            <div className="sticky top-6 space-y-4">
              {/* Main Booking Card */}
              <div className="rounded-2xl border border-border/50 bg-card shadow-luxury overflow-hidden">
                <div className="p-6">
                  {/* Price Header - Large and prominent */}
                  <div className="mb-6">
                    <div className="flex items-baseline gap-1">
                      <span className="price-display-lg text-foreground">
                        {formatPrice(experience.basePrice || 0, experience.currency || 'USD')}
                      </span>
                      <span className="text-muted-foreground font-medium">
                        {experience.priceType === 'per_group' ? ' / group' : ' / person'}
                      </span>
                    </div>
                    {experience.priceType === 'per_person' && (experience.minGuests || 1) > 1 && (
                      <p className="mt-2 text-sm text-muted-foreground">
                        Minimum {experience.minGuests} guests required
                      </p>
                    )}
                  </div>

                  {/* Add-ons Preview - Elegant list */}
                  {hasAddOns && (
                    <div className="mb-6 rounded-xl bg-muted/30 border border-border/30 p-4">
                      <p className="mb-3 text-sm font-semibold text-foreground flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-primary" />
                        Enhance your experience
                      </p>
                      <ul className="space-y-2.5">
                        {experience.addOns!.slice(0, 3).map((addon) => (
                          <li key={addon.id} className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">{addon.name}</span>
                            <span className="font-semibold text-foreground">
                              +{formatPrice(addon.price || addon.priceInCents || 0, experience.currency || 'USD')}
                            </span>
                          </li>
                        ))}
                        {experience.addOns!.length > 3 && (
                          <li className="text-sm text-primary font-medium">
                            +{experience.addOns!.length - 3} more options available
                          </li>
                        )}
                      </ul>
                    </div>
                  )}

                  {/* CTA Button - Premium styling */}
                  <Link
                    to={bookLink}
                    onClick={() => {
                      if (experience) {
                        trackBookingStarted(experience.id, experience.name);
                        updateAbandonmentStep('date', experience.id);
                      }
                    }}
                    className="btn-luxury w-full text-base"
                  >
                    <Calendar className="h-5 w-5" />
                    Check Availability
                    <ChevronRight className="h-5 w-5" />
                  </Link>

                  {/* Secondary Actions */}
                  <div className="mt-4 flex gap-3">
                    <button 
                      onClick={handleSave}
                      className={cn(
                        'btn-luxury-secondary flex-1 text-sm py-2.5',
                        isSaved && 'border-primary/30 bg-primary/5 text-primary'
                      )}
                    >
                      <Heart className={cn('h-4 w-4', isSaved && 'fill-current')} />
                      {isSaved ? 'Saved' : 'Save'}
                    </button>
                    <button 
                      onClick={handleShare}
                      className="btn-luxury-secondary flex-1 text-sm py-2.5"
                    >
                      {shareStatus === 'copied' ? (
                        <>
                          <CheckCircle className="h-4 w-4 text-success" />
                          <span className="text-success">Copied!</span>
                        </>
                      ) : shareStatus === 'shared' ? (
                        <>
                          <CheckCircle className="h-4 w-4 text-success" />
                          <span className="text-success">Shared!</span>
                        </>
                      ) : (
                        <>
                          <Share2 className="h-4 w-4" />
                          Share
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Trust Indicators - Prominent section */}
                <div className="border-t border-border/30 bg-muted/20 px-6 py-5">
                  <ul className="space-y-3.5">
                    <TrustIndicator 
                      icon={Shield}
                      text="Free cancellation up to 24 hours before"
                    />
                    <TrustIndicator 
                      icon={Zap}
                      text="Instant confirmation"
                    />
                    <TrustIndicator 
                      icon={CreditCard}
                      text="Reserve now, pay later available"
                    />
                  </ul>
                </div>
              </div>

              {/* Host Card - Elegant design */}
              {config?.property && (
                <div className="rounded-2xl border border-border/50 bg-card shadow-soft p-5">
                  <p className="mb-3 text-[10px] font-semibold uppercase tracking-elegant text-muted-foreground">
                    Hosted by
                  </p>
                  <div className="flex items-center gap-4">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/10">
                      <span className="font-serif text-xl font-semibold text-primary">
                        {config.property.name?.charAt(0) || 'H'}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-foreground truncate">
                        {config.property.name}
                      </p>
                      {config.property.location?.city && (
                        <p className="text-sm text-muted-foreground truncate">
                          {config.property.location.city}
                          {config.property.location.state && `, ${config.property.location.state}`}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>
    </Layout>
  );
}

// ============================================
// HELPER COMPONENTS
// ============================================

function ImageOverlayBadges({ experience }: { experience: ExperienceDetail }) {
  return (
    <>
      {/* Category Badge - Glassmorphism */}
      {experience.category && (
        <div className="absolute left-4 top-4 hidden lg:block">
          <span className="inline-flex items-center gap-1.5 rounded-full glass px-3.5 py-2 text-sm font-medium text-foreground shadow-sm">
            <span className="text-base">{experience.category.icon}</span>
            {experience.category.name}
          </span>
        </div>
      )}
      {/* Rating Badge */}
      {experience.averageRating && experience.totalReviews > 0 && (
        <div className="absolute right-4 top-4">
          <span className="inline-flex items-center gap-1.5 rounded-full glass px-3 py-2 text-sm font-semibold shadow-sm">
            <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
            {experience.averageRating.toFixed(1)}
          </span>
        </div>
      )}
    </>
  );
}

function StatCard({ 
  icon: Icon, 
  value, 
  label,
  iconClassName
}: { 
  icon: React.ComponentType<{ className?: string }>; 
  value: string;
  label: string;
  iconClassName?: string;
}) {
  return (
    <div className="flex items-center gap-3 rounded-xl bg-muted/50 border border-border/30 px-4 py-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-background shadow-sm">
        <Icon className={cn('h-5 w-5 text-muted-foreground', iconClassName)} />
      </div>
      <div>
        <p className="font-semibold text-foreground">{value}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
      </div>
    </div>
  );
}

function ContentSection({ 
  title, 
  icon: Icon,
  iconColor,
  children 
}: { 
  title: string;
  icon?: React.ComponentType<{ className?: string }>;
  iconColor?: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h2 className="mb-5 flex items-center gap-2.5 font-serif text-xl font-medium text-foreground">
        {Icon && <Icon className={cn('h-5 w-5', iconColor || 'text-primary')} />}
        {title}
      </h2>
      {children}
    </section>
  );
}

function CheckItem({ 
  text, 
  variant = 'default' 
}: { 
  text: string; 
  variant?: 'default' | 'included' | 'highlight';
}) {
  const colors = {
    default: 'bg-muted text-muted-foreground',
    included: 'bg-success/10 text-success',
    highlight: 'bg-primary/10 text-primary',
  };

  return (
    <div className="flex items-start gap-3">
      <div className={cn(
        'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
        colors[variant]
      )}>
        <Check className="h-3 w-3" strokeWidth={2.5} />
      </div>
      <span className="text-foreground text-[15px]">{text}</span>
    </div>
  );
}

function TrustIndicator({ 
  icon: Icon, 
  text 
}: { 
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>; 
  text: string;
}) {
  return (
    <li className="flex items-center gap-3">
      <div className="trust-badge-icon">
        <Icon className="h-3.5 w-3.5" strokeWidth={2} />
      </div>
      <span className="text-sm text-muted-foreground">{text}</span>
    </li>
  );
}

function LoadingSkeleton() {
  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-6 h-5 w-40 rounded-lg skeleton-shine" />
      
      <div className="grid gap-8 lg:grid-cols-12 lg:gap-10">
        {/* Main Content */}
        <div className="lg:col-span-7 xl:col-span-8 space-y-8">
          <div className="aspect-[16/10] rounded-2xl skeleton-shine" />
          <div className="space-y-4">
            <div className="h-7 w-28 rounded-full skeleton-shine" />
            <div className="h-12 w-3/4 rounded-lg skeleton-shine" />
            <div className="h-6 w-full rounded-lg skeleton-shine" />
          </div>
          <div className="flex gap-4">
            <div className="h-16 w-36 rounded-xl skeleton-shine" />
            <div className="h-16 w-36 rounded-xl skeleton-shine" />
            <div className="h-16 w-36 rounded-xl skeleton-shine" />
          </div>
          <div className="space-y-4 pt-8">
            <div className="h-7 w-48 rounded-lg skeleton-shine" />
            <div className="h-4 w-full rounded skeleton-shine" />
            <div className="h-4 w-full rounded skeleton-shine" />
            <div className="h-4 w-2/3 rounded skeleton-shine" />
          </div>
        </div>
        
        {/* Sidebar */}
        <div className="lg:col-span-5 xl:col-span-4">
          <div className="rounded-2xl border border-border/30 bg-card shadow-luxury p-6 space-y-6">
            <div className="h-10 w-36 rounded-lg skeleton-shine" />
            <div className="h-24 w-full rounded-xl skeleton-shine" />
            <div className="h-14 w-full rounded-xl skeleton-shine" />
            <div className="flex gap-3">
              <div className="h-11 flex-1 rounded-xl skeleton-shine" />
              <div className="h-11 flex-1 rounded-xl skeleton-shine" />
            </div>
            <div className="space-y-3 pt-4 border-t border-border/30">
              <div className="h-5 w-full rounded skeleton-shine" />
              <div className="h-5 w-full rounded skeleton-shine" />
              <div className="h-5 w-3/4 rounded skeleton-shine" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ErrorState({ message, backLink }: { message: string; backLink: string }) {
  return (
    <div className="mx-auto max-w-md py-20 text-center animate-fade-in">
      <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-destructive/10">
        <AlertCircle className="h-10 w-10 text-destructive" />
      </div>
      <h2 className="font-serif text-2xl font-medium text-foreground mb-3">
        Experience Not Found
      </h2>
      <p className="text-muted-foreground mb-8 leading-relaxed">{message}</p>
      <Link
        to={backLink}
        className="btn-luxury"
      >
        <ArrowLeft className="h-4 w-4" />
        Browse All Experiences
      </Link>
    </div>
  );
}
